// AI-GEN-BEGIN - 优化后的 Axios 拦截器配置
import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { isObject } from 'lodash-es'
import useUserStore from '@/store/modules/user'
import {
  getUrlParam,
} from '@/utils'
import {
  getLecoomisSeesionExpired,
  getSSToken,
  setLecoomisSeesionExpired,
} from '@/utils/auth'

interface itfRes<T = {}[] | {} | []> {
  data: {
    code: number
    msg: string | undefined
    data: T
  }
}
declare module 'axios' {
  interface AxiosResponse extends itfRes {}
}

// ===================== 工具函数 =====================
/**
 * 统一的会话过期处理
 */
// AI-GEN-BEGIN - 修复会话过期处理：lecooLogout 已确保清理+跳转，此处仅做防重入
function handleSessionExpired() {
  const expired = getLecoomisSeesionExpired()

  if (expired === undefined || Number(expired) <= 0) {
    setLecoomisSeesionExpired(1)
    const userStore = useUserStore()
    userStore.lecooLogout().finally(() => {
      setLecoomisSeesionExpired(0)
    })
  }
}
// AI-GEN-END

/**
 * 从错误响应体提取业务文案（兼容 JSON / ArrayBuffer / message|msg）
 */
// AI-GEN-BEGIN - Spec 接口错误体优先展示 message
function extractResponseMessage(data: unknown): string | undefined {
  if (data == null) {
    return undefined
  }
  if (typeof data === 'string') {
    try {
      const json = JSON.parse(data) as { message?: string, msg?: string }
      return json.message || json.msg
    }
    catch {
      return undefined
    }
  }
  if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
    try {
      const text = new TextDecoder().decode(data)
      if (!text.trimStart().startsWith('{')) {
        return undefined
      }
      const json = JSON.parse(text) as { message?: string, msg?: string }
      return json.message || json.msg
    }
    catch {
      return undefined
    }
  }
  if (typeof data === 'object') {
    const obj = data as { message?: string, msg?: string }
    return obj.message || obj.msg
  }
  return undefined
}
// AI-GEN-END

/**
 * 统一的错误消息处理
 */
function handleErrorMessage(error: AxiosError) {
  let message = error.message

  // AI-GEN-BEGIN - 优先展示后端业务错误文案
  const bizMessage = extractResponseMessage(error.response?.data)
  if (bizMessage) {
    message = bizMessage
  }
  else if (message === 'Network Error') {
    message = '请检查网络'
  }
  else if (message.includes('timeout')) {
    message = '接口请求超时'
  }
  else if (message.includes('Request failed with status code')) {
    const statusCode = message.substring(message.length - 3)
    message = `接口 ${statusCode} 异常`
  }
  // AI-GEN-END

  // 处理 419 状态码（会话过期）
  if (error.response?.status === 419) {
    handleSessionExpired()
    return
  }

  ElMessage({
    message,
    type: 'error',
  })
}

// ===================== commonApi 实例 =====================
const commonApi: AxiosInstance = axios.create({
  baseURL: (import.meta.env.DEV && String(import.meta.env.VITE_OPEN_PROXY) === 'true') ? '/' : import.meta.env.VITE_APP_MIS_API_BASEURL,
  timeout: 1000 * 60 * 5,
  responseType: 'json',
})

const customCommonHeaders = {
  Authorization: '',
}
const authParam = getUrlParam('auth')
customCommonHeaders.Authorization = authParam?.includes('+')
  ? authParam?.replace('+', ' ') || ''
  : authParam || ''

// 请求拦截器
commonApi.interceptors.request.use(
  (request: InternalAxiosRequestConfig) => {
    const userStore = useUserStore()

    if (userStore.isLogin && request.headers) {
      request.headers.Token = userStore.token
    }

    if (request.headers) {
      request.headers.Authorization = customCommonHeaders.Authorization
    }

    return request
  },
)

// 响应拦截器 - 优化版
commonApi.interceptors.response.use(
  (response: AxiosResponse) => {
    // 检查是否是 HTML 错误页面
    if (!Object.prototype.hasOwnProperty.call(response.data, 'data') && !isObject(response.data)) {
      if (typeof response.data === 'string' && response.data.includes('无权访问')) {
        ElMessage({
          message: '无权访问',
          type: 'error',
          duration: 5 * 1000,
        })
        return Promise.reject(new Error('无权访问'))
      }
      // 非预期的响应格式，直接返回
      return Promise.resolve(response.data)
    }

    const code = response.data.code?.toString()

    // 成功：现网 200；LEC-39 Spec 为 0；mailReportFilter 为 OK
    // AI-GEN-BEGIN
    if (code === '200' || code === '0' || code === 'OK') {
      return Promise.resolve(response.data)
    }
    // AI-GEN-END

    // 特殊状态码处理
    switch (code) {
      case '201':
        // 商户列表在用，表示提交时已存在数据
        return Promise.reject(response.data)

      case '401':
        return Promise.reject(response.data)

      case '600':
        // 会话过期
        handleSessionExpired()
        return Promise.reject(new Error('会话已过期'))

      default:
        // 3xx 和 4xx 范围的状态码
        if (code?.startsWith('3') || code?.startsWith('4')) {
          return Promise.resolve(response.data)
        }

        // 其他错误状态码
        if (response.data.msg) {
          ElMessage({
            message: response.data.msg,
            type: 'error',
          })
        }
        return Promise.reject(response.data)
    }
  },
  (error: AxiosError) => {
    handleErrorMessage(error)
    return Promise.reject(error)
  },
)

// ===================== serviceBlobApi 实例（文件下载）=====================
const serviceBlobApi: AxiosInstance = axios.create({
  baseURL: (import.meta.env.DEV && String(import.meta.env.VITE_OPEN_PROXY) === 'true') ? '/' : import.meta.env.VITE_APP_MIS_API_BASEURL,
  timeout: 1000 * 60 * 5,
  responseType: 'arraybuffer',
})

serviceBlobApi.interceptors.request.use(
  (request: InternalAxiosRequestConfig) => {
    const userStore = useUserStore()

    if (userStore.isLogin && request.headers) {
      request.headers.Token = userStore.token
    }

    if (request.headers) {
      request.headers.Authorization = customCommonHeaders.Authorization
    }

    return request
  },
)

serviceBlobApi.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.status === 200) {
      return Promise.resolve(response.data)
    }

    // 错误处理
    const errorMessage = response.statusText || '系统异常'
    return Promise.reject(new Error(errorMessage))
  },
  (error: AxiosError) => {
    handleErrorMessage(error)
    return Promise.reject(error)
  },
)

// ===================== api 实例 =====================
const api = axios.create({
  baseURL: (import.meta.env.DEV && String(import.meta.env.VITE_OPEN_PROXY) === 'true') ? '/' : import.meta.env.VITE_APP_MIS_API_BASEURL,
  timeout: 1000 * 60,
  responseType: 'json',
})

const customHeaders = {
  'Content-Type': 'application/json',
  'Authorization': '',
}
customHeaders.Authorization = getSSToken() || ''

api.interceptors.request.use(
  (request: InternalAxiosRequestConfig) => {
    const userStore = useUserStore()

    if (userStore.isLogin && request.headers) {
      request.headers.Token = userStore.token
    }

    if (request.headers) {
      request.headers['Content-Type'] = customHeaders['Content-Type']
      request.headers.Authorization = customHeaders.Authorization
    }

    return request
  },
)

// 响应拦截器 - 修复逻辑
api.interceptors.response.use(
  (response: AxiosResponse<any, any>) => {
    const code = response.data.code

    // 成功
    if (code === 200) {
      return Promise.resolve(response.data)
    }

    // 401/403 等认证错误
    if (code?.toString().startsWith('40')) {
      setLecoomisSeesionExpired(1)
      useUserStore().lecooLogout()
      return Promise.reject(new Error('认证失败'))
    }

    // 其他错误
    if (response.data.msg) {
      ElMessage({
        message: response.data.msg,
        type: 'error',
      })
    }

    return Promise.reject(response.data)
  },
  (error: AxiosError) => {
    handleErrorMessage(error)
    return Promise.reject(error)
  },
)

// ===================== oldMisService 实例 =====================
const oldMisService = axios.create({
  baseURL: (import.meta.env.DEV && String(import.meta.env.VITE_OPEN_PROXY) === 'true') ? '/' : import.meta.env.VITE_APP_MIS_API_BASEURL,
  timeout: 1000 * 60,
  responseType: 'json',
})

const customOldHeaders = {
  'Content-Type': 'application/json',
  'Authorization': '',
}
customOldHeaders.Authorization = getSSToken() || ''

oldMisService.interceptors.request.use(
  (request: InternalAxiosRequestConfig) => {
    const userStore = useUserStore()

    if (userStore.isLogin && request.headers) {
      request.headers.Token = userStore.token
    }

    if (request.headers) {
      request.headers['Content-Type'] = customOldHeaders['Content-Type']
      request.headers.Authorization = customOldHeaders.Authorization
    }

    return request
  },
)

oldMisService.interceptors.response.use(
  (response: AxiosResponse<any, any>) => {
    // 直接返回原始数据（旧接口格式不同）
    return Promise.resolve(response.data)
  },
  (error: AxiosError) => {
    handleErrorMessage(error)
    return Promise.reject(error)
  },
)

export {
  api,
  commonApi,
  oldMisService,
  serviceBlobApi,
}
// AI-GEN-END
